import MatchedMap from 'utilities/src/matchedMap';

import type { IIconsProps, SizeType } from '../../../../../Icon';

import { InputStateEnum } from '../../../utils/state';
import { InputSizeEnum } from '../../../utils/size';
import { InputTypeEnum } from '../../../utils/types';
import { InputThemeEnum, type InputThemeProps } from '../../../utils/themes';

/**
 * Common Classes
 */

const fieldClasses = 'rounded-[4px] flex-nowrap outline-offset-[-1px] cursor-default';

const fieldInputTypeClassesMap = new MatchedMap([
  [InputTypeEnum.Input, 'items-center'],
  [InputTypeEnum.Textarea, 'items-start'],
]);

/**
 * Icon Classes
 */

const fieldIconClassesMap = new MatchedMap([
  [InputThemeEnum.Dark, 'fill-site-neutrals-100'],
  [InputThemeEnum.Light, 'fill-site-neutrals-600'],
]);

const darkFieldIconStateClassesMap = new MatchedMap([
  [InputStateEnum.Default, 'bg-site-neutrals-100'],
  [InputStateEnum.Disabled, 'bg-site-neutrals-100'],
  [InputStateEnum.Error, 'bg-site-error-600'],
  [InputStateEnum.Success, 'bg-site-success-500'],
]);

const lightFieldIconStateClassesMap = new MatchedMap([
  [InputStateEnum.Default, 'bg-site-neutrals-600'],
  [InputStateEnum.Disabled, 'bg-site-neutrals-600'],
  [InputStateEnum.Error, 'bg-site-error-700'],
  [InputStateEnum.Success, 'bg-site-success-600'],
]);

const fieldIconStateClassesMap = new MatchedMap([
  [InputThemeEnum.Dark, darkFieldIconStateClassesMap],
  [InputThemeEnum.Light, lightFieldIconStateClassesMap],
]);

/**
 * Size Classes
 */

const fieldSizeClassesMap = new MatchedMap([
  [InputSizeEnum.Small, 'gap-2 px-2 py-1.5 [&_input]:site-paragraph-m [&_textarea]:site-paragraph-m'],
  [InputSizeEnum.Medium, 'gap-2 px-2 py-2 [&_input]:site-paragraph-m [&_textarea]:site-paragraph-m'],
  [InputSizeEnum.Large, 'gap-4 px-3 py-3 [&_input]:site-paragraph-l [&_textarea]:site-paragraph-l'],
]);

/**
 * Color Classes
 */

const fieldBackgroundClassesMap = new MatchedMap([
  [
    InputThemeEnum.Dark,
    'text-site-neutrals-0 bg-site-neutrals-900 [&_input]:bg-site-neutrals-900 [&_input]:placeholder:text-site-neutrals-200 [&_textarea]:bg-site-neutrals-900 [&_textarea]:placeholder:text-site-neutrals-200',
  ],
  [
    InputThemeEnum.Light,
    'text-site-neutrals-900 bg-white [&_input]:bg-white [&_input]:placeholder:text-site-neutrals-400 [&_textarea]:bg-white [&_textarea]:placeholder:text-site-neutrals-400',
  ],
]);

/**
 * State Classes
 */

const defaultStateClassesMap = new MatchedMap([
  [InputStateEnum.Default, ''],
  [InputStateEnum.Disabled, ''],
  [InputStateEnum.Error, 'outline-2 outline-offset-[-2px]'],
  [InputStateEnum.Success, 'outline-2 outline-offset-[-2px]'],
]);

/**
 * Theme State Classes
 */

const darkStateClassesMap = new MatchedMap([
  [InputStateEnum.Default, 'outline-site-neutrals-400 hover:outline-site-neutrals-100'],
  [InputStateEnum.Disabled, '!outline-site-neutrals-500'],
  [InputStateEnum.Error, '!outline-site-error-600'],
  [InputStateEnum.Success, '!outline-site-success-500'],
]);

const lightStateClassesMap = new MatchedMap([
  [InputStateEnum.Default, 'outline-site-neutrals-100 hover:outline-site-neutrals-400'],
  [InputStateEnum.Disabled, '!outline-site-neutrals-100'],
  [InputStateEnum.Error, '!outline-site-error-700 !outline-2'],
  [InputStateEnum.Success, '!outline-site-success-600'],
]);

const themeStateClassesMap = new MatchedMap([
  [InputThemeEnum.Dark, darkStateClassesMap],
  [InputThemeEnum.Light, lightStateClassesMap],
]);

/**
 * Theme Focus Classes
 */

const defaultFocusClasses =
  '[&:not(:focus-within)]:outline-1 focus-within:outline-1 focus-within:-outline-offset-1 focus-within:shadow-ring';

const darkFocusClasses =
  'focus-within:outline-site-neutrals-25 focus-within:hover:outline-site-neutrals-25 focus-within:shadow-site-primary-200';
const lightFocusClasses =
  'focus-within:outline-site-neutrals-600 focus-within:hover:outline-site-neutrals-600 focus-within:!shadow-site-primary-50 [&:not(:focus-within)]:outline-site-neutrals-100';

const themeFocusClassesMap = new MatchedMap([
  [InputThemeEnum.Dark, darkFocusClasses],
  [InputThemeEnum.Light, lightFocusClasses],
]);

/**
 * Theme Filled Classes
 */

const defaultFilledClasses = 'outline-2 outline-offset-[-2px]';

const darkFilledClasses = 'outline-site-neutrals-25 hover:outline-site-neutrals-25';
const lightFilledClasses = 'outline-site-neutrals-600 hover:outline-site-neutrals-600';

const themeFilledClassesMap = new MatchedMap([
  [InputThemeEnum.Dark, darkFilledClasses],
  [InputThemeEnum.Light, lightFilledClasses],
]);

/**
 * Theme Field Icons
 */

const fieldStateIconMap = new MatchedMap<InputStateEnum, IIconsProps['iconName']>([
  [InputStateEnum.Default, undefined],
  [InputStateEnum.Disabled, undefined],
  [InputStateEnum.Error, 'Warning'],
  [InputStateEnum.Success, 'CheckCircle'],
]);

const fieldIconSize = new MatchedMap<InputSizeEnum, SizeType>([
  [InputSizeEnum.Small, 'IconS'],
  [InputSizeEnum.Medium, 'IconSM'],
  [InputSizeEnum.Large, 'IconSM'],
]);

/**
 * Site Theme
 */

const inputFieldSiteTheme = ({ inputState, inputTheme, inputSize, inputType }: InputThemeProps) => ({
  fieldThemeClasses: fieldInputTypeClassesMap.get(InputTypeEnum[inputType]).concat(' ', fieldClasses),

  fieldBehaviorDefaultThemeClasses: themeStateClassesMap
    .get(InputThemeEnum[inputTheme])
    .get(InputStateEnum[inputState]),
  fieldBehaviorFocusThemeClasses: themeFocusClassesMap
    .get(InputThemeEnum[inputTheme])
    .concat(' ', defaultFocusClasses),
  fieldBehaviorFilledThemeClasses: themeFilledClassesMap
    .get(InputThemeEnum[inputTheme])
    .concat(' ', defaultFilledClasses),

  fieldStateThemeClasses: defaultStateClassesMap.get(InputStateEnum[inputState]),

  fieldSizeThemeClasses: fieldSizeClassesMap.get(InputSizeEnum[inputSize]),
  fieldBackgroundThemeClasses: fieldBackgroundClassesMap.get(InputThemeEnum[inputTheme]),

  fieldVariantThemeClasses: '',

  fieldIconThemeClasses: fieldIconClassesMap.get(InputThemeEnum[inputTheme]),
  fieldIconStateThemeClasses: fieldIconStateClassesMap
    .get(InputThemeEnum[inputTheme])
    .get(InputStateEnum[inputState]),

  fieldThemeStateIcon: fieldStateIconMap.get(InputStateEnum[inputState]),
  fieldIconThemeSize: fieldIconSize.get(InputSizeEnum[inputSize]),
});

export default inputFieldSiteTheme;
